import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

// B&W Floorplan for services hero background
const FLOORPLAN_IMAGE = "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/wwqzg6e9_floorplan.png";

// Custom furniture sketch images for carousel
const furnitureSketchImages = [
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/qscs0oph_1.png",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/07rvdbo3_2.png",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/qwplbe7i_3.png",
];

const coreServices = [
  "Space Planning",
  "Construction Drawings",
  "Council Submissions",
  "Project Management",
  "3D Rendering",
];

const designStages = [
  {
    stage: "01",
    title: "Inception",
    description: "Understanding client requirements, constraints, budget, and defining the project scope.",
  },
  {
    stage: "02",
    title: "Concept Design",
    description: "Developing initial design concepts, space planning, and reviewing feasibility and costs.",
  },
  {
    stage: "03",
    title: "Design Development",
    description: "Refining the design with detailed selections, technical drawings, and coordination.",
  },
  {
    stage: "04",
    title: "Documentation & Procurement",
    description: "Preparing council submissions, technical documentation, and managing tender processes.",
  },
  {
    stage: "05",
    title: "Construction",
    description: "Overseeing construction, ensuring quality, compliance, and execution of design intent.",
  },
  {
    stage: "06",
    title: "Close Out",
    description: "Finalizing the project with handover documentation and completion certification.",
  },
];

const services = [
  {
    id: "corporate",
    title: "Corporate Design",
    subtitle: "Professional Spaces That Inspire",
    description:
      "Create work environments that boost productivity, reflect your brand, and attract top talent. From open-plan offices to executive suites, we design corporate spaces that work as hard as your team.",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/ek5uv03n_Modern%20Prescient%20Lounge%20with%20Warm%20Accents.png",
    features: [
      "Office layout optimization",
      "Brand integration",
      "Breakout and collaboration areas",
      "Reception and client spaces",
      "Employee wellness considerations",
    ],
  },
  {
    id: "residential",
    title: "Residential Design",
    subtitle: "Luxury Living, Tailored to You",
    description:
      "Your home should be a reflection of who you are. We create bespoke residential interiors that combine luxury with livability, crafting spaces that feel both sophisticated and deeply personal.",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/q6czf4k2_IMG_2492.webp",
    features: [
      "Complete home design",
      "Kitchen and bathroom design",
      "Custom storage solutions",
      "Material and finish selection",
      "Furniture curation",
    ],
  },
  {
    id: "restaurant",
    title: "Restaurant & Cafe Design",
    subtitle: "Memorable Hospitality Spaces",
    description:
      "In hospitality, ambiance is everything. We design restaurants, cafes, and bars that enhance the dining experience, create Instagram-worthy moments, and keep customers coming back.",
    image: "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/j8s20asd_Render%2002%20%281%29.png",
    features: [
      "Concept development",
      "Customer flow optimization",
      "Kitchen and service area planning",
      "Mood and lighting design",
      "Outdoor dining spaces",
    ],
  },
  {
    id: "turnkey",
    title: "Turnkey Solutions",
    subtitle: "From Concept to Completion",
    description:
      "Leave the complexity to us. Our turnkey service manages every aspect of your renovation—from initial design through construction to final styling. We coordinate with our trusted partners to deliver seamless results.",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_nh-spaces/artifacts/p0sh9gjf_Modern%20Executive%20Conference%20Room.png",
    features: [
      "Full project coordination",
      "Contractor management",
      "Timeline and budget control",
      "Quality assurance",
      "Stress-free experience",
    ],
  },
  {
    id: "furniture",
    title: "Custom Furniture",
    subtitle: "Bespoke Pieces, Unique to You",
    description:
      "When off-the-shelf won't do, we create custom furniture designed specifically for your space and needs. Working with skilled manufacturers, we bring unique pieces to life.",
    image: "carousel",
    features: [
      "Custom design development",
      "Material selection",
      "Prototype review",
      "Quality manufacturing",
      "Installation coordination",
    ],
  },
];

export default function ServicesPage() {
  const [furnitureIndex, setFurnitureIndex] = useState(0);

  // Rotate furniture sketches
  useEffect(() => {
    const timer = setInterval(() => {
      setFurnitureIndex((prev) => (prev + 1) % furnitureSketchImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero Section with B&W Floorplan Background */}
      <section className="relative min-h-[70vh] flex items-end pb-20" data-testid="services-hero">
        <div className="absolute inset-0">
          <img
            src={FLOORPLAN_IMAGE}
            alt="Architectural Floor Plan"
            className="w-full h-full object-cover grayscale opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/30 via-cream/60 to-cream" />
        </div>
        
        <div className="container-custom relative z-10 pt-32">
          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body opacity-0 animate-fade-in">
            What We Do
          </p>
          <h1 className="font-heading text-5xl md:text-7xl text-black opacity-0 animate-fade-in animate-delay-100">
            Comprehensive Design
            <br />
            <span className="italic text-warmBrown">Services</span>
          </h1>
        </div>
      </section>

      {/* Core Services Overview */}
      <section className="py-16 bg-primary" data-testid="core-services">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {coreServices.map((service, index) => (
              <div
                key={service}
                className="text-center"
                data-testid={`core-service-${index}`}
              >
                <span className="text-white/80 text-sm font-body uppercase tracking-wider">
                  {service}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Process Section */}
      <section className="section-padding bg-cream" data-testid="design-process-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
              Our Approach
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-black">
              The Design
              <br />
              <span className="italic text-warmBrown">Process</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0">
            {designStages.map((item, index) => (
              <div
                key={item.stage}
                className="group p-8 border-b border-black/5 md:border-r md:last:border-r-0 lg:[&:nth-child(3)]:border-r-0 lg:[&:nth-child(6)]:border-r-0"
                data-testid={`design-stage-${index}`}
              >
                <span className="font-heading text-5xl text-warmBrown/20 group-hover:text-warmBrown/50 transition-colors duration-500">
                  {item.stage}
                </span>
                <h3 className="font-heading text-xl text-black mt-4 mb-3">
                  {item.title}
                </h3>
                <p className="text-black/50 text-sm font-body leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List — No icons */}
      {services.map((service, index) => (
        <section
          key={service.id}
          className={`section-padding ${
            index % 2 === 0 ? "bg-soft-beige" : "bg-cream"
          }`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="container-custom">
            <div
              className={`grid md:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? "md:flex-row-reverse" : ""
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? "md:order-2" : ""}>
                <p className="text-warmBrown uppercase tracking-[0.2em] text-xs font-body mb-6">
                  {service.subtitle}
                </p>
                <h2 className="font-heading text-3xl md:text-5xl text-black mb-6">
                  {service.title}
                </h2>
                <p className="text-black/60 font-body leading-relaxed mb-8">
                  {service.description}
                </p>

                {/* Features List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, fIndex) => (
                    <div
                      key={feature}
                      className="flex items-center gap-3"
                      data-testid={`feature-${service.id}-${fIndex}`}
                    >
                      <Check className="text-warmBrown flex-shrink-0" size={16} />
                      <span className="text-black/70 font-body text-sm">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Core Services - hide for furniture */}
                {service.id !== "furniture" && (
                <div className={`p-6 ${index % 2 === 0 ? "bg-soft-beige" : "bg-cream"}`}>
                  <p className="text-warmBrown text-xs uppercase tracking-wider mb-4 font-body">
                    Included Services
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {coreServices.map((core) => (
                      <span
                        key={core}
                        className="text-xs text-warmBrown border border-warmBrown/30 px-3 py-1 font-body"
                      >
                        {core}
                      </span>
                    ))}
                  </div>
                </div>
                )}
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? "md:order-1" : ""}`}>
                {service.image === "carousel" ? (
                  <div className="aspect-[4/3] overflow-hidden relative" style={{ backgroundColor: '#F5F3EF' }}>
                    {furnitureSketchImages.map((img, imgIdx) => (
                      <img
                        key={imgIdx}
                        src={img}
                        alt={`Custom Furniture Sketch ${imgIdx + 1}`}
                        className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ${
                          imgIdx === furnitureIndex ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    ))}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {furnitureSketchImages.map((_, dotIdx) => (
                        <div
                          key={dotIdx}
                          className={`rounded-full transition-all duration-500 ${
                            dotIdx === furnitureIndex ? "w-6 h-1.5 bg-warmBrown" : "w-1.5 h-1.5 bg-black/20"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="section-padding bg-warmBrown" data-testid="services-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-5xl text-white mb-6">
            Let's Discuss Your Project
          </h2>
          <p className="text-white/80 font-body text-lg max-w-xl mx-auto mb-8">
            Every great space starts with a conversation. Tell us about your
            vision and let's explore the possibilities together.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-white text-black hover:bg-champagne transition-all duration-500 uppercase tracking-[0.2em] text-xs px-8 py-4 font-semibold"
            data-testid="services-cta-button"
          >
            Start Your Project
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
